import { useFormik } from "formik";
import {
  TextField,
  MenuItem,
  Button,
  Grid,
  Typography,
  Box,
  Breadcrumbs,
  Divider,
  Card,
  IconButton,
  Autocomplete,
  InputLabel,
  FormControl,
  Select,
  OutlinedInput,
  ListItemText,
  FormHelperText,
  InputAdornment,
  Paper,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useNavigate, useParams } from "react-router-dom";
import Link from "@mui/material/Link";
import { useRef, useState } from "react";
import Layout from "../../layout/Layout";
import { AlertMessage } from "../../components/AlertMessage";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Editor styles

import { apiData } from "../../services/api_service";
import {
  fetchProductDropdownAPI,
  fetchBuyingPriceAPI,
  insertOrderAPI,
  fetchOrdernumberAPI,
  fetchOrderByIdAPI,
  updateOrderByIdAPI,
  fetchvendorByIdAPI,
  termsAndConditionsAPI,
  fetchVendorsAPI,
  fetchVendorDDAPI,
  fetchVendorAPI,
  insertPoAPI,
  updatePoByIdAPI,
  fetchPoByIdAPI,
  storesDropdownAPI,
} from "../../config";
import { useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import * as Yup from "yup";
import dayjs from "dayjs";
import { formatAED } from "../../utils/format";
import { LoadingButton } from "@mui/lab";
import { Backdrop, CircularProgress } from "@mui/material";
import { emirates } from "../../components/constants";

const isKnownEmirate = (value) =>
  emirates.some((emirate) => emirate.name === value);

const resolveEmirateName = (value) => {
  if (value === null || value === undefined || value === "") return "";

  const valueText = String(value);
  const emirate = emirates.find(
    (item) =>
      String(item.id) === valueText ||
      item.name.toLowerCase() === valueText.toLowerCase()
  );

  return emirate ? emirate.name : valueText;
};

const validateUaePhone = (value, isRequired = true) => {
  if (!value) return isRequired ? "Mobile Number is required" : true;

  // Remove spaces/hyphens for clean checking
  const clean = value.replace(/[\s-]/g, "");

  // Accept any country's phone number: optional leading '+', 7-15 digits
  const phoneRegex = /^\+?[0-9]{7,15}$/;
  if (!phoneRegex.test(clean)) {
    return "Enter a valid phone number";
  }

  return true; // ✅ valid
};

const validationSchema = Yup.object({
  orderDate: Yup.string().required("Order Date is required"),
  vendor_id: Yup.string().nullable().required("Vendor is required"),
  name: Yup.string()
    .required("Supplier Name is required")
    .min(3, "Minimum 3 characters")
    .max(100, "Maximum 100 characters")
    .matches(
      /^[a-zA-Z0-9 _-]+$/,
      "Only letters, numbers, spaces, hyphen (-) and underscore (_) are allowed"
    ),
  company_name: Yup.string().nullable().max(150, "Maximum 150 characters"),

  phone_number: Yup.string()
    .nullable()
    .test("valid-uae-phone", (value, ctx) => {
      if (!value) return true;
      const result = validateUaePhone(value, false);
      return result === true ? true : ctx.createError({ message: result });
    }),

  email: Yup.string().nullable(),

  address: Yup.string()
    .required("Street Address is required")
    .min(3, "Minimum 3 characters")
    .max(100, "Maximum 100 characters")
    .matches(
      /^[a-zA-Z0-9 _,-]+$/,
      "Only letters, numbers, spaces, hyphen (-), comma (,) and underscore (_) are allowed"
    ),

  emirate: Yup.string().nullable(),

  po_box: Yup.string().nullable(),

  vat_no: Yup.string().nullable(),

  items: Yup.array()
    .of(
      Yup.object().shape({
        productId: Yup.string().nullable().required("Product is required"),
        buyingPrice: Yup.number()
          .required("Buying Price is required")
          .moreThan(0, "Buying Price cannot be 0"),
        quantity: Yup.number()
          .required("Quantity is required")
          .moreThan(0, "Quantity cannot be 0"),
        subtotal: Yup.number()
          .required("Subtotal is required")
          .min(0, "Cannot be negative"),
      })
    )
    .min(1, "At least 1 product must be selected"),

  taxAmount: Yup.number()
    .required("Tax amount is required")
    .min(0, "Cannot be negative"),

  term_id: Yup.string().required("Terms & Conditions is required"),
  store_id: Yup.number().required("Store is required"),

  // notes: Yup.string().max(500, "Maximum 500 characters allowed"),
});

const PurchaseOrderAdd = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const alertRef = useRef();
  const { id } = useParams();
  const navigate = useNavigate();

  const [productList, setProductList] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [searchTerm, setSearchTerm] = useState([""]);
  const [storeOptions, setStoreOptions] = useState([]);

  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      orderDate: dayjs().format("YYYY-MM-DD"),
      vendor_id: null,
      orderNumber: "",
      name: "",
      company_name: "",
      phone_number: "",
      email: "",
      address: "",
      vat_no: "",
      po_box: "",
      emirate: "",
      emirateOption: "",
      items: [{ productId: "", buyingPrice: "", quantity: "1", subtotal: "" }],
      taxType: "5", // 'percent' | 'amount'
      taxAmount: "",
      shippingCharges: "0.00",
      discount: "0.00",
      notes: "",
      term_id: "",
      terms: "",
      orderStatus: "Completed",
      store_id: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        values.user_id = user.emp_id;
        let result;
        if (id) {
          result = await apiData("put", `${updatePoByIdAPI}/${id}`, values);
        } else {
          result = await apiData("post", insertPoAPI, values);
        }

        if (result.success) {
          alertRef.current.triggerAlert(
            JSON.stringify({
              message: result.msg,
              messageType: "success",
            })
          );
          setTimeout(() => {
            navigate("/purchase-order/list");
            setTimeout(() => window.location.reload(), 200);
          }, 7000);
        } else {
          alertRef.current.triggerAlert(
            JSON.stringify({
              message: result.msg,
              messageType: "error",
            })
          );
        }
      } catch (error) {
        alertRef.current.triggerAlert(
          JSON.stringify({
            message: error.msg,
            messageType: "error",
          })
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const [initialValues, setInitialValues] = useState(null);

  useEffect(() => {
    async function fetchOrder() {
      if (id) {
        const result = await apiData("get", `${fetchPoByIdAPI}/${id}`);

        if (result.success && result.data) {
          const order = result.data.order;
          const items = result.data.items || [];

          const formattedDate = order.order_date
            ? dayjs(order.order_date).format("YYYY-MM-DD")
            : dayjs().format("YYYY-MM-DD");

          const values = {
            vendor_id: order.vendor_id || null,
            orderDate: formattedDate,
            name: order.name || "",
            company_name: order.company_name || "",
            store_id: order.store_id || "",
            phone_number: order.phone_number || "",
            email: order.email || "",
            address: order.address || "",
            emirate: resolveEmirateName(order.city || order.emirate || ""),
            emirateOption: (() => {
              const val = resolveEmirateName(order.city || order.emirate || "");
              if (!val) return "";
              return isKnownEmirate(val) ? val : "Others";
            })(),
            po_box: order.po_box || "",
            vat_no: order.vat_no || "",
            items: items.map((item) => ({
              productId: item.product_id || "",
              productName: item.product_name || "", // if you have product_name
              buyingPrice: parseFloat(item.buying_price || 0),
              quantity: item.quantity || 0,
              subtotal: parseFloat(item.total_price || 0),
            })),
            taxType: "5",
            subtotalSummary: parseFloat(order.subtotal || 0),
            taxAmount: parseFloat(order.tax_amount || 0),
            shippingCharges: parseFloat(order.shipping_amount || 0),
            discount: parseFloat(order.discount_amount || 0),
            grandTotal: parseFloat(order.grand_total || 0),
            notes: order.notes || "",
            terms: order.terms || "",
            term_id: order.term_id || "",
          };
          setInitialValues(values);
          formik.setValues(values);
        } else {
          alertRef.current.triggerAlert(
            JSON.stringify({
              message: result.msg,
              messageType: "error",
            })
          );
        }
      }
    }

    fetchOrder();
  }, [id]);

  const addRow = () => {
    formik.setFieldValue("items", [
      ...formik.values.items,
      { productId: "", buyingPrice: "", quantity: 1, subtotal: "" },
    ]);
    setSearchTerm([...searchTerm, ""]);
  };

  const removeRow = (idx) => {
    const nextItems = [...formik.values.items];
    const nextSearch = [...searchTerm];
    nextItems.splice(idx, 1);
    nextSearch.splice(idx, 1);
    formik.setFieldValue("items", nextItems);
    setSearchTerm(nextSearch);
    recalcSummary(nextItems);
  };

  const recalcSummary = (
    items,
    shippingOverride,
    discountOverride,
    taxTypeOverride
  ) => {
    const subtotal = items.reduce(
      (sum, row) => sum + (parseFloat(row.subtotal) || 0),
      0
    );

    const discount =
      parseFloat(discountOverride ?? formik.values.discount) || 0;
    const discountedSubtotal = subtotal - discount;
    const netSubtotal = discountedSubtotal > 0 ? discountedSubtotal : 0;

    const taxRate = parseFloat(taxTypeOverride ?? formik.values.taxType) || 0;
    const taxAmount = (netSubtotal * taxRate) / 100;

    const shipping =
      parseFloat(shippingOverride ?? formik.values.shippingCharges) || 0;

    // Grand total = subtotal - discount + tax + shipping
    const grandTotal = netSubtotal + taxAmount + shipping;

    formik.setValues({
      ...formik.values,
      items,
      subtotalSummary: netSubtotal, // ✅ This excludes shipping
      taxAmount,
      shippingCharges: shipping, // ✅ keep shipping separate
      grandTotal,
    });
  };

  const handleProductSelect = async (idx, product) => {
    if (!product) return;

    try {
      const result = await apiData(
        "get",
        `${fetchBuyingPriceAPI}/${product.id}`
      );

      const updatedItems = [...formik.values.items];

      // Use the buying price from the API response, not from the product object
      const latestBuyingPrice = result.data?.[0]?.buying_price;
      const price = latestBuyingPrice ? Number(latestBuyingPrice) : 0;

      updatedItems[idx] = {
        ...updatedItems[idx],
        productId: product.id,
        productName: product.product_name,
        sku: product.sku,
        buyingPrice: price,
        quantity: updatedItems[idx].quantity || 1,
        subtotal:
          price && updatedItems[idx].quantity
            ? (price * updatedItems[idx].quantity).toFixed(2)
            : "0.00",
        maxQuantity: product.quantity || 0,
      };

      formik.setFieldValue("items", updatedItems);
      recalcSummary(updatedItems);
    } catch (error) {
      console.error("Failed to fetch product unit price:", error);
    }
  };

  const handleQuantityChange = (idx, value) => {
    const updatedItems = [...formik.values.items];
    updatedItems[idx].quantity = value;
    updatedItems[idx].subtotal =
      (parseFloat(updatedItems[idx].buyingPrice) || 0) * (value || 0);
    recalcSummary(updatedItems);
  };

  const fetchProducts = async (search = "") => {
    try {
      const result = await apiData(
        "get",
        `${fetchProductDropdownAPI}?search=${search}`
      );

      if (result.success) {
        setProductList(result.data.result);
      }
    } catch (error) {
      console.error("Failed to fetch units:", error);
    }
  };

  const fetchStores = async () => {
    try {
      const result = await apiData("get", storesDropdownAPI);
      if (result.success) {
        setStoreOptions(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch stores:", error);
    }
  };

  async function fetchVendors() {
    const result = await apiData("get", fetchVendorDDAPI);
    if (result.data.result) {
      setVendorList(result.data.result);
    }
  }

  const [termsAndConditions, setTermsAndConditions] = useState([]);
  const fetchTermsAndConditions = async (existingTermId = null) => {
    try {
      const payload = { termsType: 1 };
      const result = await apiData("get", termsAndConditionsAPI, payload);
      if (result.success) {
        const termsList = result.data.result || [];
        setTermsAndConditions(termsList);
        if (existingTermId) {
          // If editing, use the term saved in the order
          const existingTerm = termsList.find(
            (term) => term.id === existingTermId
          );
          if (existingTerm) {
            formik.setFieldValue("term_id", existingTerm.id);
            formik.setFieldValue("terms", existingTerm.description || "");
          }
        } else {
          // Otherwise, use default
          const defaultTerm = termsList.find((term) => term.isDefault === 1);
          if (defaultTerm) {
            formik.setFieldValue("term_id", defaultTerm.id);
            formik.setFieldValue("terms", defaultTerm.description || "");
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch terms and conditions:", error);
    }
  };
  useEffect(() => {
    // fetchNextOrderNumber();
    fetchVendors();
    fetchProducts();
    fetchStores();
    fetchTermsAndConditions();
  }, []);

  const handleVendorChange = (vendor) => {
    formik.setFieldValue("vendor_id", vendor ? vendor.vendor_id : "");
    formik.setFieldValue("name", vendor ? vendor.name : "");
    formik.setFieldValue("company_name", vendor ? vendor.company_name || "" : "");
    formik.setFieldValue("address", vendor ? vendor.address : "");
    const vendorEmirate = vendor
      ? resolveEmirateName(vendor.city || vendor.emirate || "")
      : "";
    formik.setFieldValue("emirate", vendorEmirate);
    formik.setFieldValue(
      "emirateOption",
      vendorEmirate
        ? isKnownEmirate(vendorEmirate)
          ? vendorEmirate
          : "Others"
        : ""
    );
    formik.setFieldValue("email", vendor ? vendor.email : "");
    formik.setFieldValue("phone_number", vendor ? vendor.phone_number : "");
    formik.setFieldValue("po_box", vendor ? vendor.po_box : "");
    formik.setFieldValue("vat_no", vendor ? vendor.vat_no : "");
  };

  let debounceTimer = null;
  const debounce = (fn, delay = 400) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(fn, delay);
  };

  return (
    <Layout>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {/* <Box sx={{ p: { xs: 2, sm: 4, md: 6 } }}> */}
      <Grid className="employee-form">
        <AlertMessage ref={alertRef} />

        {/* Page Title + Breadcrumb */}
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              Purchase Order
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Purchase Orders</Typography>
              <Typography color="text.primary">Add</Typography>
            </Breadcrumbs>
          </Grid>

          {/* Actions */}
          <Grid
            textAlign="right"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <LoadingButton
              size="medium"
              variant="contained"
              color="secondary"
              className="button_first"
              onClick={() => navigate("/purchase-order/list")}
            >
              Purchase Order List
            </LoadingButton>
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              sx={{
                fontWeight: "bold",
              }}
              startIcon={<ArrowBackIosIcon />}
            >
              BACK
            </LoadingButton>
          </Grid>
        </Grid>

        <form onSubmit={formik.handleSubmit}>
          <Card variant="outlined" sx={{ mt: 4, p: { xs: 2, sm: 3 } }}>
            {/* Order Details */}
            <Typography variant="h6" mb={4} gutterBottom>
              Purchase Order Details
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileDatePicker
                    label="Date of Purchase Order"
                    value={
                      formik.values.orderDate
                        ? dayjs(formik.values.orderDate)
                        : null
                    }
                    inputFormat="DD-MM-YYYY"
                    onChange={(date) => {
                      formik.setFieldValue(
                        "orderDate",
                        date ? date.format("YYYY-MM-DD") : ""
                      );
                    }}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} lg={4} md={6}>
                <Autocomplete
                  options={vendorList}
                  getOptionLabel={(option) => option.name}
                  value={
                    vendorList?.find(
                      (c) => c.vendor_id == formik.values.vendor_id
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleVendorChange(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Supplier"
                      variant="outlined"
                      error={
                        formik.touched.vendor_id &&
                        Boolean(formik.errors.vendor_id)
                      }
                      helperText={
                        formik.touched.vendor_id && formik.errors.vendor_id
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Supplier Name"
                  name="name"
                  value={formik.values.name}
                  onChange={(e) => {
                    let value = e.target.value;
                    value = value.replace(/\b\w/g, (char) =>
                      char.toUpperCase()
                    );
                    formik.setFieldValue("name", value);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  InputProps={{
                    style: { textTransform: "capitalize" },
                  }}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="company_name"
                  value={formik.values.company_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.company_name &&
                    Boolean(formik.errors.company_name)
                  }
                  helperText={
                    formik.touched.company_name && formik.errors.company_name
                  }
                />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Supplier Mobile"
                  name="phone_number"
                  value={formik.values.phone_number}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.phone_number &&
                    Boolean(formik.errors.phone_number)
                  }
                  helperText={
                    formik.touched.phone_number && formik.errors.phone_number
                  }
                />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Supplier Email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={6}>
                <TextField
                  fullWidth
                  label="Street Address"
                  name="address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.address && Boolean(formik.errors.address)
                  }
                  helperText={formik.touched.address && formik.errors.address}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={formik.values.emirateOption === "Others" ? 3 : 6}>
                <TextField
                  name="emirateOption"
                  select
                  label="Select Emirate"
                  fullWidth
                  value={formik.values.emirateOption}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue("emirateOption", value);
                    formik.setFieldValue(
                      "emirate",
                      value === "Others" ? "" : value
                    );
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.emirate && Boolean(formik.errors.emirate)
                  }
                  helperText={formik.touched.emirate && formik.errors.emirate}
                >
                  {emirates.map((emirate) => (
                    <MenuItem key={emirate.id} value={emirate.name}>
                      {emirate.name}
                    </MenuItem>
                  ))}
                  <MenuItem value="Others">Others</MenuItem>
                </TextField>
              </Grid>

              {formik.values.emirateOption === "Others" && (
                <Grid item xs={12} md={6} lg={3}>
                  <TextField
                    fullWidth
                    name="emirate"
                    label="Please specify Emirate/City/Country"
                    placeholder="Enter emirate, city or country"
                    value={formik.values.emirate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.emirate && Boolean(formik.errors.emirate)
                    }
                    helperText={
                      formik.touched.emirate && formik.errors.emirate
                    }
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  fullWidth
                  label="P.O. Box"
                  name="po_box"
                  value={formik.values.po_box}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.po_box && Boolean(formik.errors.po_box)}
                  helperText={formik.touched.po_box && formik.errors.po_box}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  fullWidth
                  label="VAT No."
                  name="vat_no"
                  value={formik.values.vat_no}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.vat_no && Boolean(formik.errors.vat_no)}
                  helperText={formik.touched.vat_no && formik.errors.vat_no}
                />
              </Grid>

              <Grid item xs={12} lg={6} md={6}>
                <Autocomplete
                  options={storeOptions}
                  getOptionLabel={(option) => option.store_name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={
                    storeOptions.find((s) => s.id === formik.values.store_id) ||
                    null
                  }
                  onChange={(e, newValue) => {
                    formik.setFieldValue(
                      "store_id",
                      newValue ? newValue.id : ""
                    );
                  }}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      {option.store_name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select a Store"
                      fullWidth
                      error={
                        formik.touched.store_id &&
                        Boolean(formik.errors.store_id)
                      }
                      helperText={
                        formik.touched.store_id && formik.errors.store_id
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: <>{params.InputProps.endAdornment}</>,
                      }}
                    />
                  )}
                  ListboxProps={{
                    style: { maxHeight: 200, overflowY: "auto" },
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Product Selection */}
            <Typography variant="h6" gutterBottom>
              Product Selection
            </Typography>

            {/* Table Header */}
            <Grid
              container
              spacing={1}
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              <Grid item md={5}>
                <Typography variant="caption" fontWeight={600}>
                  Product Name / SKU
                </Typography>
              </Grid>
              <Grid item md={2}>
                <Typography variant="caption" fontWeight={600}>
                  Buying Price
                </Typography>
              </Grid>
              <Grid item md={2}>
                <Typography variant="caption" fontWeight={600}>
                  Quantity
                </Typography>
              </Grid>
              <Grid item md={2}>
                <Typography variant="caption" fontWeight={600}>
                  Subtotal
                </Typography>
              </Grid>
            </Grid>

            {/* Dynamic Items */}
            {formik.values.items.map((row, idx) => (
              <Grid container spacing={2} key={idx} sx={{ mt: 1 }}>
                {/* Product Name / SKU */}
                <Grid item xs={12} sm={12} md={5}>
                  {/* Label for small screens */}
                  <Typography
                    variant="caption"
                    sx={{
                      display: { xs: "block", md: "none" },
                      fontWeight: 500,
                    }}
                  >
                    Product Name / SKU
                  </Typography>
                  <Autocomplete
                    options={[
                      ...productList.filter(
                        (p) =>
                          !formik.values.items.some(
                            (item, i) => i !== idx && item.productId == p.id
                          )
                      ),
                      // Always include currently selected value
                      ...(row.productId &&
                        !productList.some((p) => p.id == row.productId)
                        ? [productList.find((p) => p.id == row.productId)]
                        : []),
                    ]}
                    getOptionLabel={({ product_name, sku, color, size }) =>
                      `${product_name} ${sku} ${color} ${size}`
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    value={
                      productList.find((p) => p.id == row.productId) || null
                    }
                    inputValue={searchTerm[idx]}
                    onInputChange={(e, value, reason) => {
                      const updated = [...searchTerm];
                      updated[idx] = value;
                      setSearchTerm(updated);

                      if (reason === "input") {
                        debounce(() => fetchProducts(value), 400);
                      } else if (reason === "clear" || reason === "reset") {
                        debounce(() => fetchProducts(""), 400);
                      }
                    }}
                    onChange={(e, newValue) => {
                      if (newValue) {
                        handleProductSelect(idx, newValue);
                      } else {
                        const updatedItems = [...formik.values.items];
                        updatedItems[idx] = {
                          ...updatedItems[idx],
                          productId: null,
                          buyingPrice: 0,
                          quantity: 0,
                          subtotal: 0,
                        };
                        formik.setFieldValue("items", updatedItems);
                        recalcSummary(updatedItems);
                      }
                    }}
                    // renderOption={(props, option) => {
                    //   const size = option.size || "NA";
                    //   const color = option.color || "NA";

                    //   return (
                    //     <li
                    //       {...props}
                    //       key={option.id}
                    //       style={{
                    //         display: "flex",
                    //         alignItems: "center",
                    //         gap: 10,
                    //         padding: "6px 10px",
                    //         borderBottom: "1px solid #eee",
                    //         backgroundColor: "#fff",
                    //         borderRadius: 4,
                    //         cursor: "pointer",
                    //         fontSize: 14,
                    //       }}
                    //     >
                    //       <img
                    //         src={option.product_image}
                    //         alt={option.product_name}
                    //         style={{
                    //           width: 36,
                    //           height: 36,
                    //           objectFit: "cover",
                    //           borderRadius: 6,
                    //           border: "1px solid #ccc",
                    //         }}
                    //       />
                    //       <div
                    //         style={{ display: "flex", flexDirection: "column" }}
                    //       >
                    //         <span style={{ fontWeight: 500, color: "#333" }}>
                    //           {option.product_name}{" "}
                    //           <span style={{ color: "#555" }}>
                    //             (SKU: {option.sku})
                    //           </span>
                    //         </span>
                    //         <span style={{ fontSize: 12, color: "#007BFF" }}>
                    //           Size: {size} , Color: {color}
                    //         </span>
                    //       </div>
                    //     </li>
                    //   );
                    // }}
                    renderOption={(props, option, { selected }) => {
                      const size = option.size || "NA";
                      const color = option.color || "NA";

                      return (
                        <li
                          {...props}
                          key={option.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "6px 10px",
                            borderBottom: "1px solid #eee",
                            borderRadius: 4,
                            cursor: "pointer",
                            backgroundColor: selected ? "#e3f2fd" : "#fff", // 🔵 highlight selected
                          }}
                        >
                          <img
                            src={option.product_image}
                            alt={option.product_name}
                            style={{
                              width: 36,
                              height: 36,
                              objectFit: "cover",
                              borderRadius: 6,
                              border: "1px solid #ccc",
                            }}
                          />

                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <span style={{ fontWeight: 500 }}>
                              {option.product_name}
                              <span style={{ color: "#555" }}>
                                {" "}
                                (SKU: {option.sku})
                              </span>
                            </span>

                            <span style={{ fontSize: 12, color: "#1976d2" }}>
                              Size: {size}, Color: {color}
                            </span>
                          </div>
                        </li>
                      );
                    }}
                    // renderInput={(params) => (
                    //   <TextField
                    //     {...params}
                    //     label="Select a Product"
                    //     fullWidth
                    //     onBlur={() =>
                    //       formik.setFieldTouched(
                    //         `items[${idx}].productId`,
                    //         true
                    //       )
                    //     }
                    //     error={
                    //       formik.touched.items?.[idx]?.productId &&
                    //       Boolean(formik.errors.items?.[idx]?.productId)
                    //     }
                    //     helperText={
                    //       formik.touched.items?.[idx]?.productId &&
                    //       formik.errors.items?.[idx]?.productId
                    //     }
                    //     InputProps={{
                    //       ...params.InputProps,
                    //       endAdornment: (
                    //         <>
                    //           {!formik.values.items[idx].productId &&
                    //             searchTerm[idx] && (
                    //               <IconButton
                    //                 size="small"
                    //                 onClick={(event) => {
                    //                   event.stopPropagation();
                    //                   const updated = [...searchTerm];
                    //                   updated[idx] = "";
                    //                   setSearchTerm(updated);
                    //                   fetchProducts("");
                    //                 }}
                    //               >
                    //                 <CloseIcon fontSize="small" />
                    //               </IconButton>
                    //             )}
                    //           {params.InputProps.endAdornment}
                    //         </>
                    //       ),
                    //     }}
                    //   />
                    // )}
                    renderInput={(params) => {
                      const selectedProduct = productList.find(
                        (p) => p.id == row.productId
                      );

                      return (
                        <TextField
                          {...params}
                          label="Select a Product"
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: selectedProduct ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  paddingLeft: 6,
                                }}
                              >
                                <img
                                  src={selectedProduct.product_image}
                                  alt={selectedProduct.product_name}
                                  style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 4,
                                    objectFit: "cover",
                                    border: "1px solid #ccc",
                                  }}
                                />
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <span>
                                    {selectedProduct.product_name} (SKU:{" "}
                                    {selectedProduct.sku})
                                  </span>
                                  <span
                                    style={{ fontSize: 12, color: "#1976d2" }}
                                  >
                                    Size: {selectedProduct.size || "NA"}, Color:{" "}
                                    {selectedProduct.color || "NA"}
                                  </span>
                                </div>
                              </div>
                            ) : null,
                          }}
                          inputProps={{
                            ...params.inputProps,
                            value: selectedProduct
                              ? ""
                              : params.inputProps.value,
                          }}
                        />
                      );
                    }}
                    ListboxProps={{
                      style: { maxHeight: 200, overflowY: "auto" },
                    }}
                  />
                </Grid>

                {/* Buying Price */}
                <Grid item xs={6} sm={6} md={2}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: { xs: "block", md: "none" },
                      fontWeight: 500,
                    }}
                  >
                    Buying Price
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={row.buyingPrice}
                    onChange={(e) => {
                      const value = e.target.value;
                      const updatedItems = [...formik.values.items];
                      updatedItems[idx] = {
                        ...updatedItems[idx],
                        buyingPrice: value,
                        subtotal: (value * (row.quantity || 0)).toFixed(2),
                      };
                      formik.setFieldValue("items", updatedItems);
                      recalcSummary(updatedItems);
                    }}
                    error={
                      formik.touched.items?.[idx]?.buyingPrice &&
                      Boolean(formik.errors.items?.[idx]?.buyingPrice)
                    }
                    helperText={
                      formik.touched.items?.[idx]?.buyingPrice &&
                      formik.errors.items?.[idx]?.buyingPrice
                    }
                  />
                </Grid>

                {/* Quantity */}
                <Grid item xs={6} sm={6} md={2}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: { xs: "block", md: "none" },
                      fontWeight: 500,
                    }}
                  >
                    Quantity
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={row.quantity}
                    inputProps={{ min: 0 }}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 0;
                      handleQuantityChange(idx, value);
                    }}
                    error={
                      formik.touched.items?.[idx]?.quantity &&
                      Boolean(formik.errors.items?.[idx]?.quantity)
                    }
                    helperText={
                      formik.touched.items?.[idx]?.quantity &&
                      formik.errors.items?.[idx]?.quantity
                    }
                    onFocus={(e) => e.target.select()}
                  />
                </Grid>

                {/* Subtotal */}
                <Grid item xs={6} sm={6} md={2}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: { xs: "block", md: "none" },
                      fontWeight: 500,
                    }}
                  >
                    Subtotal
                  </Typography>
                  <TextField
                    fullWidth
                    type="text"
                    value={formatAED(row.subtotal)}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                {/* Delete Button */}
                <Grid item xs={12} sm={12} md={1} textAlign="right">
                  <IconButton
                    onClick={() => removeRow(idx)}
                    disabled={formik.values.items.length === 1}
                  >
                    <DeleteOutlineOutlinedIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Box sx={{ mt: 2 }}>
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={addRow}
                variant="outlined"
              >
                Add Product
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Summary */}
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}></Grid>
              <Grid item xs={12} md={6}>
                {/* Subtotal */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                  <Grid item xs={6}>
                    <Typography>Subtotal</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Typography>{formatAED(formik.values.subtotalSummary)}</Typography>
                  </Grid>
                </Grid>

                {/* Tax */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                  <Grid item xs={3}>
                    <Typography>Tax</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      name="taxType"
                      value={formik.values.taxType}
                      onChange={(e) => {
                        recalcSummary(
                          formik.values.items,
                          undefined,
                          undefined,
                          e.target.value
                        );
                        formik.setFieldValue("taxType", e.target.value);
                      }}
                    >
                      <MenuItem value="5">5% (Percent)</MenuItem>
                      <MenuItem value="0">0% (Percent)</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={5} textAlign="right">
                    <Typography>{formatAED(formik.values.taxAmount)}</Typography>
                  </Grid>
                </Grid>

                {/* Shipping Charges */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                  <Grid item xs={6}>
                    <Typography>Shipping Charges</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <TextField
                      size="small"
                      name="shippingCharges"
                      type="number"
                      value={formik.values.shippingCharges}
                      inputProps={{
                        inputMode: "decimal",
                        style: { textAlign: "right" },
                      }}
                      sx={{ width: 140 }}
                      onChange={(e) => {
                        let value = e.target.value;
                        recalcSummary(
                          formik.values.items,
                          value,
                          formik.values.discount,
                          formik.values.taxType
                        );

                        if (/^\d*\.?\d{0,2}$/.test(value)) {
                          formik.setFieldValue("shippingCharges", value);
                        }
                      }}
                      onFocus={(e) => e.target.select()}
                      onBlur={(e) => {
                        formik.setFieldTouched("shippingCharges", true);
                        let val = parseFloat(e.target.value || 0);
                        if (!isNaN(val)) {
                          val = val.toFixed(2);
                          formik.setFieldValue("shippingCharges", val);
                        } else {
                          formik.setFieldValue("shippingCharges", "");
                        }
                      }}
                      error={
                        formik.touched.shippingCharges &&
                        Boolean(formik.errors.shippingCharges)
                      }
                    />
                  </Grid>
                </Grid>

                {/* Grand Total */}
                <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <Typography fontWeight={700}>Grand Total</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Typography fontWeight={700}>
                      {formatAED(formik.values.grandTotal)}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Terms & Conditions
            </Typography>
            <Grid
              item
              xs={12}
              md={4}
              lg={4}
              textAlign="left"
              className="po_lebleterm"
            >
              <FormControl
                className="form_control"
                sx={{ width: "100%" }}
                error={formik.touched.term_id && Boolean(formik.errors.term_id)} // put error on FormControl
              >
                {/* <InputLabel id="demo-multiple-checkbox-label">
                  Terms & Conditions
                </InputLabel> */}
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  value={formik.values.term_id}
                  onChange={(event) => {
                    formik.setFieldValue("term_id", event.target.value);
                    const terms = termsAndConditions.find(
                      (term) => term.id === event.target.value
                    );
                    formik.setFieldValue("terms", terms?.description || "");
                  }}
                  input={<OutlinedInput />}
                >
                  {termsAndConditions?.map((terms) => (
                    <MenuItem key={terms?.id} value={terms?.id}>
                      <ListItemText primary={terms?.title} />
                    </MenuItem>
                  ))}
                </Select>

                {/* This is where the error text shows */}
                {formik.touched.term_id && formik.errors.term_id && (
                  <FormHelperText>{formik.errors.term_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            {formik.values.terms !== "" && (
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                // textAlign={`right`}
                // display={`flex`}
                // justifyContent={`flex-end`}
                mt={2}
              >
                <ReactQuill
                  theme="snow"
                  value={formik.values.terms}
                  onChange={(content) => formik.setFieldValue("terms", content)} // Store as HTML
                  style={{ height: "200px", marginBottom: "40px" }}
                />
                {formik.touched.terms && formik.errors.terms && (
                  <p style={{ color: "red", fontSize: "12px" }}>
                    {formik.errors.terms}
                  </p>
                )}
              </Grid>
            )}
            {/* Submit */}
            <Box display="flex" justifyContent="center" mt={{ xs: 14, md: 10 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                Purchase Order
              </Button>
            </Box>
          </Card>
        </form>
      </Grid>
      {/* </Box> */}
    </Layout>
  );
};

export default PurchaseOrderAdd;
